import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getVocabulariesDataList } from '../../../api/vocabulary';
import { GET_ALL_QUERY_PARAMS, InspectionStatuses } from '../../../constants';
import { VocabularyItem, setSchedules, setVocabulary } from '../../../redux/slices/generalSlice';
import { RootState } from '../../../redux/root';
import { getFranchiseList } from '../../../api/franchise';
import { getReportManagers } from '../../../api/reports';
import { setFranchises, setManagers, setProjects, setProjectsLoading } from '../../../redux/slices/projectSlice';
import { getClientDocuments, getClientPendingProjects, getContactProjects } from '../../../api/projects';
import { normalizeProjectPermits, showTableLoading } from '../utils';
import { InspectionItemType, PermitInspectionsType, PermitTicketItem, TicketItem } from '../types';
import { setPermitInspectionsList } from '../../../redux/slices/inspectionsSlice';
import { setAllPermitsWithDocs, setDocsLoading } from '../../../redux/slices/documentsSlice';
import { getScheduling } from '../../../api/scheduling';
import { getAllConditions } from '../../../api/conditions';
import { getAllClientActivities } from '../../../api/activities';
import { setActivities, setActivitiesLoading } from '../../../redux/slices/clientActivitiesSlice';
import { setConditions, setConditionsLoading } from '../../../redux/slices/conditionsSlice';
import { getAllInvoices } from '../../../api/fees';
import { getAllTickets } from '../../../api/tickets';
import { setPermitTicketsList } from '../../../redux/slices/ticketsSlice';
import { setClientProjecPermitList, setInvoicesLoading, setPermitInvoicesList } from '../../../redux/slices/feesSlice';
import { useGetAllInspectionByClientQuery } from '../../../redux/rtkQuery/apiSlice';

type Options = {
  vocabulary?: boolean;
  projects?: boolean;
  invoices?: boolean;
  documents?: boolean;
  conditions?: boolean;
  tickets?: boolean;
  scheduling?: boolean;
  inspections?: boolean;
  activities?: boolean;
};

const inFlight = {
  vocabulary: false,
  projects: false,
  franchise: false,
  managers: false,
  invoices: false,
  documents: false,
  conditions: false,
  tickets: false,
  scheduling: false,
  activities: false,
};

const useGetNewProjectData = (options: Options = {}) => {
  const {
    vocabulary: loadVocab = false,
    projects: loadProjectsOpt = false,
    invoices: loadInvoicesOpt = false,
    documents: loadDocsOpt = false,
    conditions: loadConditionsOpt = false,
    tickets: loadTicketsOpt = false,
    scheduling: loadSchedulingOpt = false,
    inspections: loadInspectionsOpt = false,
    activities: loadActivitiesOpt = false,
  } = options;

  const dispatch = useDispatch();
  const vocabularies = useSelector((state: RootState) => state.general.vocabulary);
  const { franchises, managers, projects } = useSelector((s: RootState) => s.projectsSlice);
  const { AllPermitsWithDocs } = useSelector((s: RootState) => s.documents);
  const schedules = useSelector((s: RootState) => s.general.schedules);
  const contact_id = useSelector((s: RootState) => s.userAuth.info.id);
  const AllPermitsWithConditions = useSelector((s: RootState) => s.conditions.AllPermitsWithConditions);
  const invoicesList = useSelector((s: RootState) => s.fees.inspectionInvoicesList);
  const clientProjecPermitList = useSelector((s: RootState) => s.fees.clientProjecPermitList);
  const permitTicketsList = useSelector((s: RootState) => s.tickets.permitTicketsList);
  const AllPermitsWithActivities = useSelector((s: RootState) => s.clientActivities.AllPermitsWithActivities);

  const { data: allInspections } = useGetAllInspectionByClientQuery(null, {
    skip: !loadInspectionsOpt,
  });

  const loadTickets = () => {
    if (!showTableLoading(permitTicketsList) || inFlight.tickets) return;
    inFlight.tickets = true;
    (async () => {
      try {
        const payload = await getAllTickets(contact_id);
        const data: PermitTicketItem[] = payload.data.map((item: TicketItem) => ({
          namePermitName: `${item.projectName}/${item.permitName}`,
          permitId: Number(item.permitId),
          address: item.projectAddress,
          status: item.permitStatus,
          quantity: Number(item.ticketQuantity),
          tickets: [],
        }));
        dispatch(setPermitTicketsList(data));
      } finally {
        inFlight.tickets = false;
      }
    })();
  };

  const loadInvoices = () => {
    if (!showTableLoading(invoicesList)) {
      if (clientProjecPermitList.length > 0) {
        dispatch(setInvoicesLoading(false));
      }
      return;
    }
    if (inFlight.invoices) return;
    inFlight.invoices = true;
    (async () => {
      try {
        const { data } = await getAllInvoices(contact_id);
        dispatch(setPermitInvoicesList(data as any));
        const projectsPermit = data.reduce((prjArr, prj) => {
          prj.permits.forEach(permit => {
            const _permit = {
              ...permit,
              invoices: permit.invoices.reduce((invArr, invoice) => {
                const feeWithVoid = invoice.fees.find(f => f.status === 'Void');
                if (!feeWithVoid) {
                  const _invoice = {
                    ...invoice,
                    status: invoice.paidAt === null ? 'Pending' : 'Paid',
                    paidAt: invoice.paidAt === null ? '' : invoice.paidAt,
                  };
                  invArr.push(_invoice);
                }
                return invArr;
              }, []),
            };
            const _prjPermit = {
              ...prj,
              permit: _permit,
              status: _permit.permitStatus,
              projectPermit: `${prj.projectName} / ${_permit.permitName}`,
              namePermitName: `${prj.projectName} / ${_permit.permitName}`,
              address: _permit.permitAddress,
              franchiseId: _permit.franchiseId,
              quantity: _permit.invoices.filter(i => i.status === 'Pending').length,
            };
            prjArr.push(_prjPermit);
          });
          return prjArr;
        }, []);
        dispatch(setClientProjecPermitList(projectsPermit));
      } catch (e) {
        dispatch(setInvoicesLoading(false));
      } finally {
        inFlight.invoices = false;
      }
    })();
  };

  const loadAllActivities = () => {
    if (AllPermitsWithActivities.length > 0) {
      dispatch(setActivitiesLoading(false));
      return;
    }
    if (inFlight.activities) return;
    inFlight.activities = true;
    (async () => {
      try {
        const payload = await getAllClientActivities();
        // @ts-ignore
        const _data = payload.data.map((item: any) => ({
          address: item.projectAddress,
          permitId: item.permitId,
          namePermitName: `${item.projectName}/${item.permitName}`,
          quantity: item.quantity,
          tableQty: item.quantity,
          status: item.permitStatus,
        }));
        dispatch(setActivities(_data));
      } catch (e) {
        dispatch(setActivitiesLoading(false));
      } finally {
        inFlight.activities = false;
      }
    })();
  };

  const loadConditions = () => {
    if (AllPermitsWithConditions.length > 0) {
      dispatch(setConditionsLoading(false));
      return;
    }
    if (inFlight.conditions) return;
    inFlight.conditions = true;
    (async () => {
      try {
        const payload = await getAllConditions();
        const _data = payload.data.map((item: any) => ({
          address: item.projectAddress,
          permitId: item.permitId,
          namePermitName: `${item.projectName}/${item.permitName}`,
          tableQty: item.quantity,
          status: item.permitStatus,
        }));
        dispatch(setConditions(_data));
      } catch (e) {
        dispatch(setConditionsLoading(false));
      } finally {
        inFlight.conditions = false;
      }
    })();
  };

  const loadScheduling = () => {
    if (schedules.length > 0 || inFlight.scheduling) return;
    inFlight.scheduling = true;
    (async () => {
      try {
        const payload = await getScheduling({
          offset: 0,
          is_active: true,
          is_deleted: false,
          limit: 100000000,
        });
        dispatch(setSchedules(payload.data.results));
      } finally {
        inFlight.scheduling = false;
      }
    })();
  };

  const loadAllDocuments = () => {
    if (!showTableLoading(AllPermitsWithDocs)) {
      dispatch(setDocsLoading(false));
      return;
    }
    if (inFlight.documents) return;
    inFlight.documents = true;
    (async () => {
      try {
        const payload = await getClientDocuments(contact_id);
        const _data = payload.data.map((item: any) => ({
          address: item.address,
          permitId: item.permitId,
          namePermitName: `${item.projectName}/${item.permitName}`,
          quantity: item.quantity,
          status: item.permitStatus,
          tableQty: item.tableQty,
        }));
        dispatch(setAllPermitsWithDocs(_data));
      } catch (e) {
        dispatch(setDocsLoading(false));
      } finally {
        inFlight.documents = false;
      }
    })();
  };

  const loadInspections = inspectionsArg => {
    const _inspections = inspectionsArg.filter(i => i.permitStatus !== 'Completed');
    let previousPermit: number = -1;
    const permitInspections: PermitInspectionsType[] = [];
    const sortedData = [..._inspections].sort((a, b) => {
      if (a.permit > b.permit) return 1;
      if (a.permit < b.permit) return -1;
      return 0;
    });
    sortedData.forEach((item: InspectionItemType) => {
      if (item.permit !== previousPermit) {
        const permit: PermitInspectionsType = {
          permitId: item.permit,
          namePermitName: `${item.projectName}/${item.permitName}`,
          address: item.projectAddress,
          inspections: [item],
          status: item.permitStatus,
          quantity: 1,
        };
        permitInspections.push(permit);
        previousPermit = item.permit;
      } else {
        const permitItem = permitInspections.find((p: PermitInspectionsType) => p.permitId === item.permit);
        permitItem.inspections.push(item);
        permitItem.quantity += 1;
      }
    });

    const _permitInspectionsSorted = permitInspections.reduce((permitArr, item) => {
      if (item.status === InspectionStatuses.NOT_SCHEDULED) {
        permitArr.unshift(item);
      } else {
        permitArr.push(item);
      }
      return permitArr;
    }, []);
    dispatch(setPermitInspectionsList(_permitInspectionsSorted));
  };

  const loadProjects = async (vocabs: VocabularyItem[], refresh?: boolean) => {
    if (projects.length > 0 && !refresh) return;
    if (inFlight.projects) return;
    inFlight.projects = true;
    dispatch(setProjectsLoading(true));
    try {
      const [payload, pending] = await Promise.all([
        getContactProjects({ contact_id, limit: 100000 }),
        getClientPendingProjects(contact_id),
      ]);
      // @ts-ignore
      const projectPermits = normalizeProjectPermits(payload.data, pending.data.results, vocabs);
      // @ts-ignore
      dispatch(setProjects(projectPermits));
    } catch (e) {
      dispatch(setProjectsLoading(false));
    } finally {
      inFlight.projects = false;
    }
  };

  const loadVocabulary = async (chainProjects: boolean) => {
    if (vocabularies.length > 0) {
      if (chainProjects && projects.length === 0) {
        // @ts-ignore
        loadProjects(vocabularies, false);
      } else if (chainProjects && !inFlight.projects) {
        dispatch(setProjectsLoading(false));
      }
      return;
    }
    if (inFlight.vocabulary) return;
    inFlight.vocabulary = true;
    try {
      const payload = await getVocabulariesDataList({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'title',
      });
      const vocabularyItems = payload.data.results;
      dispatch(setVocabulary(vocabularyItems));
      if (chainProjects) {
        // @ts-ignore
        loadProjects(vocabularyItems, true);
      }
    } finally {
      inFlight.vocabulary = false;
    }
  };

  const loadFranchise = async () => {
    if (franchises.length > 0 || inFlight.franchise) return;
    inFlight.franchise = true;
    try {
      const payload = await getFranchiseList({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'name',
      });
      dispatch(setFranchises(payload.data.results));
    } finally {
      inFlight.franchise = false;
    }
  };

  const loadManagers = async () => {
    if (managers.length > 0 || inFlight.managers) return;
    inFlight.managers = true;
    try {
      const payload = await getReportManagers();
      dispatch(setManagers(payload.data));
    } finally {
      inFlight.managers = false;
    }
  };

  useEffect(() => {
    if (loadProjectsOpt && projects.length === 0) {
      dispatch(setProjectsLoading(true));
    }
    if (loadInvoicesOpt && clientProjecPermitList.length === 0) {
      dispatch(setInvoicesLoading(true));
    }
    if (loadDocsOpt && showTableLoading(AllPermitsWithDocs)) {
      dispatch(setDocsLoading(true));
    }
    if (loadConditionsOpt && AllPermitsWithConditions.length === 0) {
      dispatch(setConditionsLoading(true));
    }
    if (loadActivitiesOpt && AllPermitsWithActivities.length === 0) {
      dispatch(setActivitiesLoading(true));
    }

    if (loadVocab || loadProjectsOpt) loadVocabulary(loadProjectsOpt);
    if (loadProjectsOpt) {
      loadFranchise();
      loadManagers();
    }
    if (loadDocsOpt) loadAllDocuments();
    if (loadSchedulingOpt) loadScheduling();
    if (loadConditionsOpt) loadConditions();
    if (loadActivitiesOpt) loadAllActivities();
    if (loadInvoicesOpt) loadInvoices();
    if (loadTicketsOpt) loadTickets();
  }, []);

  useEffect(() => {
    if (loadInspectionsOpt && allInspections) {
      loadInspections(allInspections);
    }
  }, [allInspections, loadInspectionsOpt]);

  return { loadProjects };
};

export default useGetNewProjectData;
